import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { createCompanyDataForm } from "@bca-company-data/company-data.constants";
import { SignatoryStatus } from "@bca-company-data/models/signatory-status-response";
import * as companyDataActions from "@bca-company-data/store/company-data.actions";
import { PageScrollService } from "@bca-core/modules/page-scroll/services/page-scroll.service";
import { AutoSuggestService } from "@bca-core/services/auto-suggest.service";
import { AvoTrackingService } from "@bca-core/services/avo-tracking.service";
import { RouterService } from "@bca-core/services/router.service";
import { UIService } from "@bca-core/services/ui.service";
import { APP_CONFIG, IAppConfiguration } from "@bca-shared/models/config";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import { Route, ScrollPositionId } from "@bca-shared/models/_enums";
import { ICompany } from "@bca-shared/models/company";
import { ICompanySignatory } from "@bca-shared/models/company-signatory";
import { Form } from "@bca-shared/models/form";
import { IProduct } from "@bca-shared/models/product";
import { IUser } from "@bca-shared/models/user";
import { ISuggestion } from "@bca-shared/modules/type-ahead/type-ahead.models";
import { AvoDataTransformService } from "@bca-shared/utils/avo/avo-data-transform.service";
import { AvoEventTypes } from "@bca-shared/utils/avo/avo-event-details.models";
import { AvoService } from "@bca-shared/utils/avo/avo.service";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import {
  selectAllProducts,
  selectProductsWithUsers,
  selectTotalProductCount,
  selectUserId,
  selectUsers,
} from "@bca-store/store.selectors";
import * as userDataActions from "@bca-user-data/store/user-data.actions";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subscription } from "rxjs";
import { combineLatest, filter, map, take, withLatestFrom } from "rxjs/operators";

@Component({
  selector: "bca-company-data",
  templateUrl: "company-data.component.html",
  styleUrls: ["company-data.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyDataComponent
  extends Form<ICompany>
  implements OnDestroy, OnInit
{
  selectedCompany$: Observable<ICompany>;
  differentContactPersonChecked$: Observable<boolean>;
  selectUserFromHistoryChecked$: Observable<boolean>;
  users$: Observable<IUser[]>;
  middleNames$: Observable<string[]>;
  hasMultipleUsers$: Observable<boolean>;
  signatoryStatus$: Observable<SignatoryStatus>;
  selectedUserId$: Observable<number>;
  companyLoading$: Observable<boolean>;
  companyFetching$: Observable<boolean>;
  cartProducts$: Observable<any[]>;
  cartSubTotalReceiptPrice$: Observable<number>;
  cartTax$: Observable<number>;
  cartTotalReceiptPrice$: Observable<number>;
  totalProductCount$: Observable<number>;

  private lastReportedCompanySearchError: string | undefined;
  private lastReportedEmployeeAmountError: string | undefined;

  products: IProduct[];

  signatoryStatus: SignatoryStatus;

  companySearchFn: (
    searchTerm$: Observable<string>,
  ) => Observable<ISuggestion<ICompany>[]>;

  subscriptions = new Subscription();
  externalFormURL =
    "https://www.ns.nl/zakelijk/formulieren/aanvragen-overeenkomst-ns-business-cards";

  constructor(
    private _store$: Store<IRootState>,
    private _formBuilder: FormBuilder,
    private _routerService: RouterService,
    private _uiService: UIService,
    private _autoSuggest: AutoSuggestService,
    private _translate: TranslateService,
    private _changeDetector: ChangeDetectorRef,
    private _page: PageScrollService,
    private _avoService: AvoService,
    private _avoTranslateService: AvoDataTransformService,
    private _avoTrackingService: AvoTrackingService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(APP_CONFIG) private _cartConfig: IAppConfiguration,
  ) {
    super(createCompanyDataForm(_formBuilder));
    const { subscriptions } = this;

    this.signatoryStatus$ = this._store$.pipe(
      select(({ companyData }) => companyData.signatoryStatus),
    );
    this.companySearchFn = this._autoSuggest.searchCompany.bind(_autoSuggest);
    this.middleNames$ = this._store$.pipe(
      select(({ userData }) => userData.middleNames),
    );
    this.differentContactPersonChecked$ = this._store$.pipe(
      select(({ companyData }) => companyData.differentContactPersonChecked),
    );
    this.selectUserFromHistoryChecked$ = this._store$.pipe(
      select(({ companyData }) => companyData.selectUserFromHistoryChecked),
    );
    this.selectedCompany$ = this._store$.pipe(
      select(({ companyData }) => companyData.selectedCompany),
    );
    this.companyFetching$ = this._store$.pipe(
      select(({ companyData }) => companyData.companyFetching),
    );
    this.companyLoading$ = this._store$.pipe(
      select(({ companyData }) => companyData.companyLoading),
    );
    this.users$ = this._store$.pipe(select(selectUsers));
    this.hasMultipleUsers$ = this.users$.pipe(map((users) => users.length > 1));
    this.selectedUserId$ = this._store$.pipe(select(selectUserId));
    this.cartProducts$ = this._store$.pipe(select(selectProductsWithUsers));
    this.totalProductCount$ = this._store$.pipe(select(selectTotalProductCount));
    this.cartSubTotalReceiptPrice$ = this._store$.pipe(
      select(({ receipt }) => receipt.totalPrice),
    );
    this.cartTax$ = this.cartSubTotalReceiptPrice$.pipe(
      map((subTotal) => Math.round(subTotal * this._cartConfig.taxRate)),
    );
    this.cartTotalReceiptPrice$ = this.cartSubTotalReceiptPrice$.pipe(
      combineLatest(this.cartTax$),
      map(([subTotal, tax]) => subTotal + tax),
    );

    subscriptions.add(
      this.differentContactPersonChecked$.subscribe((checked) => {
        const control = this.getControl("contactPerson");
        if (checked && control.disabled) {
          control.enable();
          this._page.scrollTo(ScrollPositionId.CONTACT_PERSON, {
            timeout: 275,
            scrollOffset: 200,
          });
        }
        if (!checked && control.enabled) {
          control.disable();
        }
      }),
    );
    subscriptions.add(
      this._store$
        .pipe(select(selectAllProducts))
        .subscribe((products: IProduct[]) => {
          this.products = products;
        }),
    );

    subscriptions.add(
      this._uiService.tabClicked$
        .pipe(filter((tab) => tab.done))
        .subscribe(() => this.validate()),
    );

    subscriptions.add(
      this._uiService.nextPageClicked$.subscribe(() => {
        this._changeDetector.markForCheck();
        this.formSubmit();
      }),
    );

    subscriptions.add(
      this._store$
        .pipe(select(({ companyData }) => companyData.signatoryStatus))
        .subscribe((signatoryStatus) => {
          this.signatoryStatus = signatoryStatus;
        }),
    );

    subscriptions.add(
      this._uiService.editProductClicked$.subscribe(() => this.validate()),
    );

    subscriptions.add(
      this._uiService.addProductClicked$.subscribe(() => {
        this.validate();
        this._store$.dispatch(
          this._createAction(new userDataActions.SelectProduct(null)),
        );
        this.resetSignatoryStatus();
        this._routerService.navigateTo(Route.COMPOSE);
      }),
    );

    subscriptions.add(
      this.selectedCompany$.subscribe((selectedCompany) => {
        if (selectedCompany) {
          this.patchValue(selectedCompany);
        } else {
          this.resetValue();
        }
        this.resetSignatoryStatus();
      }),
    );
    this._store$.dispatch(
      this._createAction(new userDataActions.FetchMiddleNames()),
    );
  }

  ngOnInit(): void {
    this.resetSignatoryStatus();
    this.handleAvoEvents("add_shipping_info");
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  resetSignatoryStatus() {
    this._store$.dispatch(
      this._createAction(new companyDataActions.ResetSignatoryStatus()),
    );
  }

  onEmployeeAmountSelected(employeeAmount: number): void {
    const { formGroup } = this;
    const company = formGroup.value as ICompany;

    this._store$.dispatch(
      this._createAction(
        new companyDataActions.SelectCompany({
          ...company,
          employeeAmount,
        }),
      ),
    );
    this.handleAvoEvents("select_click", null, employeeAmount);
  }

  formSubmit(): void {
    const valid = this.validate();

    if (!valid) {
      this.touchControls();
      this.changeDetectorRef.detectChanges();
      this.scrollToFirstInvalidInput();

      return;
    }
    if (valid) {
      this.handleAvoEvents("form_submit");
    }

    this.subscriptions.add(
      this.signatoryStatus$.pipe(
        filter(status => status !== SignatoryStatus.NIET_IN_ORDE && status !== null),
        take(1),
        withLatestFrom(this._store$.pipe(select(({ app }) => app.currentRoute))),
      ).subscribe(([_, currentRoute]) => {
        // guard: user may have navigated away via the tab stepper before the API responded
        if (currentRoute !== Route.COMPANY_DATA) { return; }
        this.handleAvoEvents("form_submit");
        this._store$.dispatch(this._createAction(new appActions.NextRoute()));
      })
    );
  }

  scrollToFirstInvalidInput() {
    const firstInvalidInput = document.querySelector("nes-error-message");
    if (firstInvalidInput) {
      firstInvalidInput.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  getCompanyErrorKey(): string | undefined {
    const formTagId = "companySearch";
    let errorMessage;
    if (
      this.isTouchedAndDirty("searchTerm") &&
      this.hasError("searchTerm", "required")
    ) {
      errorMessage = this._translate.instant(
        "COMPANY_DATA.INPUT.COMPANY.ERROR_REQUIRED",
      );
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedCompanySearchError) {
      this._avoService.formError({
        formName: "companySearch",
        formId1: formTagId,
        errorMessage: errorMessage,
      });
      this.lastReportedCompanySearchError = errorMessage;
    }

    if (!errorMessage) {
      this.lastReportedCompanySearchError = undefined;
    }

    return errorMessage;
  }
  getEmployeeAmountErrorKey(): string | undefined {
    const formTagId = "employeeAmount";
    let errorMessage;
    if (
      this.isTouchedAndDirty("employeeAmount") &&
      this.hasError("employeeAmount", "required")
    ) {
      errorMessage = this._translate.instant(
        "COMPANY_DATA.INPUT.EMPLOYEES.ERROR_REQUIRED",
      );
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedEmployeeAmountError) {
      this._avoService.formError({
        formName: "companySearch",
        formId1: formTagId,
        errorMessage: errorMessage,
      });
      this.lastReportedEmployeeAmountError = errorMessage;
    }

    if (!errorMessage) {
      this.lastReportedEmployeeAmountError = undefined;
    }

    return errorMessage;
  }

  previousRoute(): void {
    this.validate();
    this.handleAvoEvents("link_click");
    this._store$.dispatch(this._createAction(new appActions.PreviousRoute()));
  }

  goToBesteloverzicht(): void {
    this._routerService.navigateTo(Route.VERIFY);
  }

  onCompanySelected(company: ICompany): void {
    this._store$.dispatch(
      this._createAction(new companyDataActions.SelectCompany(null)),
    );
    this._store$.dispatch(
      this._createAction(new companyDataActions.FetchCompany(company)),
    );
    this._store$.dispatch(
      this._createAction(new receiptActions.DeleteCompany()),
    );
  }

  onCompanyClosed(): void {
    this._store$.dispatch(
      this._createAction(new companyDataActions.SelectCompany(null)),
    );
    this._store$.dispatch(
      this._createAction(new receiptActions.DeleteCompany()),
    );
  }

  onCompanyFocus(): void {
    this._avoService.formStart({
      formName: "Select_company",
      formId1: "bca-company-search",
    });
    this._uiService.showMobileReceiptFooter$.next(false);
  }

  onCompanyFocusOut(): void {
    this._uiService.showMobileReceiptFooter$.next(true);
  }

  onDifferentContactPersonChecked(checked: boolean): void {
    this.handleAvoEvents("selectable_click", checked);
    this._store$.dispatch(
      this._createAction(
        new companyDataActions.DifferentContactPersonChecked({ checked }),
      ),
    );
  }

  onSelectUserFromHistoryChecked(checked: boolean): void {
    // prevent selectable losing its border on a second click untill fix nessie
    this._store$.dispatch(
      this._createAction(
        new companyDataActions.SelectUserFromHistoryChecked({ checked }),
      ),
    );
  }

  onUserFromHistorySelected(userId: number): void {
    this.selectedCompany$
      .pipe(take(1), withLatestFrom(this.users$))
      .subscribe(([selectedCompany, users]) => {
        const representative = {
          ...users.find((user) => user.id === +userId),
          phoneNumber: selectedCompany.representative
            ? selectedCompany.representative.phoneNumber
            : null,
        };

        if (
          selectedCompany.representative &&
          selectedCompany.representative.id === +userId
        ) {
          Object.assign(representative, selectedCompany.representative);
        }

        this._store$.dispatch(
          this._createAction(
            new companyDataActions.SelectCompany({
              ...selectedCompany,
              representative,
            }),
          ),
        );
      });
  }

  validate(): boolean {
    const { formGroup } = this;
    const { valid } = formGroup;
    const company = formGroup.value as ICompany;

    this._store$.dispatch(
      this._createAction(
        new companyDataActions.SelectCompany(
          company.kvkNumber ? company : null,
        ),
      ),
    );

    if (!valid) {
      this._store$.dispatch(
        this._createAction(new receiptActions.DeleteCompany()),
      );
    } else {
      const companySignatory: ICompanySignatory = this.createSignatory(company);
      this._store$.dispatch(
        this._createAction(new receiptActions.AddCompany(company)),
      );
      this._store$.dispatch(
        this._createAction(
          new companyDataActions.ValidateSignatory(companySignatory),
        ),
      );
    }

    return valid;
  }

  public handleUserFormError(event) {
    this.handleAvoEvents(
      "form_error",
      null,
      null,
      event.formTagId,
      event.errorMessage,
    );
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, CompanyDataComponent);
  }

  private createSignatory(company: ICompany) {
    return {
      kvkNumber: company.kvkNumber,
      initials: company.representative.initials,
      prefix: company.representative.middleName,
      lastName: company.representative.lastName,
    };
  }

  private handleAvoEvents(
    eventType: AvoEventTypes,
    checked?: boolean,
    employeeAmount?: number,
    formTagId?: string,
    errorMessage?: string,
  ) {
    switch (eventType) {
      case "form_submit":
        this._avoService.formSubmit({
          formId1: "CompanyDetails",
          formDestination: "ns-zakelijk|checkout|4-controle",
          permissions: null,
          clickText: "Controleren",
          formName: "CompanyDetails",
        });
        break;
      case "select_click":
        this._avoService.selectClick({
          websiteSection: "ns-zakelijk|checkout|3-gegevens-bedrijf",
          description: "Aantal medewerkers binnen het bedrijf",
          uniqueName: "employeeAmount",
          clickText: employeeAmount.toString(),
          optionAmount: 6,
          selectedOption: employeeAmount.toString(),
          optionList: [
            "1",
            "2-50",
            "51 - 100",
            "101 - 500",
            "501 - 2000",
            "meer dan2000",
          ],
        });
        break;
      case "link_click":
        this._avoTrackingService.trackDownloadFormLinkClick(
          this.externalFormURL,
        );
        break;
      case "add_shipping_info":
        const shippingInfo = this._avoTranslateService.addShippingInfo(
          this.products,
        );
        this._avoService.addShippingInfo(shippingInfo);
        break;
      case "selectable_click":
        this._avoService.selectableClick({
          uniqueName: "addContactPerson: " + checked,
          description: "Is iemand anders de contactpersoon",
          websiteSection: "ns-zakelijk|checkout|3-gegevens-bedrijf",
          clickText: checked ? "ja" : "nee",
          optionList: ["ja", "nee"],
          selectedOption: checked ? "ja" : "nee",
          optionAmount: 2,
        });
        break;
      case "form_error":
        this._avoService.formError({
          formName: "company_contact_details",
          formId1: formTagId,
          errorMessage: [errorMessage],
        });
        break;
      default:
        console.warn(`Unhandled event type: ${eventType}`);
    }
  }
}
